import React, { useState } from 'react';
import { AthleteProfile } from '../types';
import { 
  X, 
  Share2, 
  Copy, 
  Check, 
  Gift, 
  MessageSquare, 
  Sparkles, 
  Users, 
  Award, 
  QrCode,
  Zap,
  CheckCircle2
} from 'lucide-react';
import { triggerHaptic } from '../utils/haptics';

interface ReferralModalProps {
  user: AthleteProfile;
  isOpen: boolean;
  onClose: () => void;
  onEarnXp?: (amount: number, source: string) => void;
}

export const ReferralModal: React.FC<ReferralModalProps> = ({
  user,
  isOpen,
  onClose,
  onEarnXp
}) => {
  const [copiedLink, setCopiedLink] = useState(false);
  const [copiedCode, setCopiedCode] = useState(false);
  const [referralCount, setReferralCount] = useState<number>(user.referralCount || 2);
  const [simulatedSuccess, setSimulatedSuccess] = useState<string | null>(null);

  if (!isOpen) return null;

  const rawCode = user.referralCode || `${(user.name || 'ATHLETE').split(' ')[0].toUpperCase()}-XP150`;
  const shareUrl = `${window.location.origin}/?ref=${rawCode}`;
  const shareText = `Join me on Playground League! Register with my code ${rawCode} to claim +100 bonus XP and link up on the court: ${shareUrl}`;

  const handleCopyLink = () => {
    triggerHaptic();
    navigator.clipboard.writeText(shareUrl);
    setCopiedLink(true);
    setTimeout(() => setCopiedLink(false), 2500);
  };

  const handleCopyCode = () => {
    triggerHaptic();
    navigator.clipboard.writeText(rawCode);
    setCopiedCode(true);
    setTimeout(() => setCopiedCode(false), 2500);
  };

  const handleNativeShare = async () => {
    triggerHaptic();
    if (navigator.share) {
      try {
        await navigator.share({
          title: 'Playground League Invitation',
          text: shareText,
          url: shareUrl,
        });
      } catch (err) {
        // User cancelled or share failed
      }
    } else {
      // Fallback SMS
      const smsUrl = `sms:?body=${encodeURIComponent(shareText)}`;
      window.open(smsUrl, '_blank');
    }
  };

  const handleSimulateReferral = () => {
    triggerHaptic();
    const newCount = referralCount + 1;
    setReferralCount(newCount);
    if (onEarnXp) {
      onEarnXp(150, 'Referral Reward (+150 XP)');
    }
    setSimulatedSuccess(`🎉 Athlete successfully signed up using code ${rawCode}! You received +150 XP!`);
    setTimeout(() => setSimulatedSuccess(null), 5000);
  };

  return (
    <div className="fixed inset-0 z-50 bg-black/80 backdrop-blur-md flex items-center justify-center p-4 animate-fadeIn">
      <div className="bg-indigo-950 border-2 border-lime-400/50 rounded-[2.5rem] w-full max-w-lg p-6 sm:p-8 text-white shadow-2xl relative overflow-hidden space-y-6">
        {/* Glow */}
        <div className="absolute top-0 right-0 -mt-10 -mr-10 w-40 h-40 bg-lime-400/20 rounded-full blur-3xl pointer-events-none" />

        {/* Close Button */}
        <button
          onClick={onClose}
          className="absolute top-5 right-5 p-2 bg-indigo-900/80 hover:bg-indigo-800 text-indigo-300 hover:text-white rounded-full transition border border-white/10"
        >
          <X className="w-5 h-5" />
        </button>

        {/* Modal Header */}
        <div className="flex items-center space-x-3 pr-8">
          <div className="p-3 bg-lime-400 text-black rounded-2xl shadow-lg shadow-lime-400/20">
            <Gift className="w-7 h-7 stroke-[2.5]" />
          </div>
          <div>
            <div className="flex items-center space-x-2">
              <span className="px-2 py-0.5 bg-lime-400/20 text-lime-300 text-[10px] font-mono font-black uppercase rounded border border-lime-400/30">
                REFERRAL REWARDS
              </span>
            </div>
            <h2 className="text-xl sm:text-2xl font-black italic uppercase text-white mt-0.5">
              Invite Friends & Earn XP
            </h2>
          </div>
        </div>

        {/* Reward Explanation Banner */}
        <div className="p-4 bg-gradient-to-r from-indigo-900/80 to-slate-900/80 rounded-2xl border border-lime-400/30 space-y-2">
          <div className="flex items-center justify-between text-xs">
            <span className="font-extrabold uppercase text-indigo-200">Your Referral Reward</span>
            <span className="font-black italic text-lime-400 text-sm">+150 XP per signup</span>
          </div>
          <p className="text-xs text-indigo-200/90 leading-relaxed font-medium">
            Share your unique invite code or referral link via text message, social media, or email. When your friend registers, you both gain instant XP bonus!
          </p>
        </div>

        {/* Referral Code & Share Link Inputs */}
        <div className="space-y-4">
          {/* Referral Code Box */}
          <div>
            <label className="block text-[11px] font-mono font-bold uppercase text-indigo-300 mb-1.5">
              Your Exclusive Referral Code
            </label>
            <div className="flex items-center space-x-2">
              <div className="flex-1 bg-black/60 border border-lime-400/40 rounded-2xl px-4 py-3 font-mono font-black text-lime-400 text-lg tracking-wider text-center select-all">
                {rawCode}
              </div>
              <button
                onClick={handleCopyCode}
                className="px-4 py-3 bg-lime-400 hover:bg-lime-300 text-black font-black uppercase rounded-2xl transition flex items-center space-x-1.5 shrink-0 text-xs shadow-md"
              >
                {copiedCode ? <Check className="w-4 h-4" /> : <Copy className="w-4 h-4" />}
                <span>{copiedCode ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          </div>

          {/* Share Link Box */}
          <div>
            <label className="block text-[11px] font-mono font-bold uppercase text-indigo-300 mb-1.5">
              Direct Invitation Link
            </label>
            <div className="flex items-center space-x-2">
              <input
                type="text"
                readOnly
                value={shareUrl}
                className="flex-1 bg-indigo-900/50 border border-white/10 rounded-2xl px-3.5 py-2.5 font-mono text-xs text-indigo-200 truncate focus:outline-none"
              />
              <button
                onClick={handleCopyLink}
                className="px-3.5 py-2.5 bg-indigo-800 hover:bg-indigo-700 text-white font-bold rounded-2xl transition flex items-center space-x-1 shrink-0 text-xs border border-white/10"
              >
                {copiedLink ? <Check className="w-4 h-4 text-lime-400" /> : <Copy className="w-4 h-4" />}
                <span>{copiedLink ? 'Copied' : 'Copy'}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Primary Action Buttons: Text / Native Share */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
          <button
            onClick={handleNativeShare}
            className="w-full py-3 px-4 bg-lime-400 hover:bg-lime-300 text-black font-black italic uppercase rounded-2xl shadow-lg shadow-lime-400/20 transition flex items-center justify-center space-x-2 text-xs tracking-wider"
          >
            <MessageSquare className="w-4 h-4 stroke-[2.5]" />
            <span>Share Link via Text / App</span>
          </button>

          <button
            onClick={handleSimulateReferral}
            className="w-full py-3 px-4 bg-indigo-900 hover:bg-indigo-800 text-white font-black italic uppercase rounded-2xl border border-white/20 transition flex items-center justify-center space-x-2 text-xs tracking-wider"
          >
            <Zap className="w-4 h-4 text-amber-400 fill-amber-400" />
            <span>Test Signup (+150 XP)</span>
          </button>
        </div>

        {/* Notification Banner on Simulated Signup */}
        {simulatedSuccess && (
          <div className="p-3 bg-emerald-500/20 border border-emerald-400/50 rounded-2xl text-xs text-emerald-300 font-bold flex items-center space-x-2 animate-fadeIn">
            <CheckCircle2 className="w-4 h-4 text-emerald-400 shrink-0" />
            <span>{simulatedSuccess}</span>
          </div>
        )}

        {/* Referral Stats Summary Footer */}
        <div className="pt-4 border-t border-white/10 grid grid-cols-2 gap-3 text-center">
          <div className="p-3 bg-indigo-900/40 rounded-2xl border border-white/5">
            <span className="text-[10px] font-mono font-bold text-indigo-300 uppercase block">Total Referred</span>
            <span className="text-xl font-black italic text-white">{referralCount} Athletes</span>
          </div>
          <div className="p-3 bg-indigo-900/40 rounded-2xl border border-white/5">
            <span className="text-[10px] font-mono font-bold text-indigo-300 uppercase block">XP Earned</span>
            <span className="text-xl font-black italic text-lime-400">+{referralCount * 150} XP</span>
          </div>
        </div>
      </div>
    </div>
  );
};
